<style scoped lang="css"></style>

<template>
  <div class="main-wrapper">
    <section class="section main-section bg-gradient-default min-vh-100 pt-3">
      <main class="container card shadow shadow-lg--hover mb-3" id="main">
        <Header title="Bienvenido a sistemas AESSSA" />
        <b-container class="mb-3">
          <b-row class="text-center">
            <b-col cols="12">
              <p>Porfavor ingrese sus datos de acceso para continuar</p>
            </b-col>
          </b-row>
        </b-container>
        <b-container class="mb-3">
          <b-row align-h="center" class="mb-3">
            <b-col v-show="errorMessage" cols="12">
              <p class="text-center">{{ errorMessage }}</p>
            </b-col>
            <b-col cols="12" md="8" lg="6">
              <b-form @submit="submitForm" v-show="!requestNewPassword">
                <b-form-group>
                  <b-form-input
                    v-model="email"
                    type="email"
                    required
                    debounce="300"
                    placeholder="Correo electronico"
                  ></b-form-input>
                </b-form-group>
                <b-form-group>
                  <b-form-input
                    v-model="password"
                    required
                    debounce="300"
                    type="password"
                    placeholder="Contraseña"
                  ></b-form-input>
                </b-form-group>
                <b-button
                  class="float-right"
                  variant="success"
                  type="submit"
                  :disabled="loading"
                >
                  <b-spinner v-if="loading" small />
                  <span v-else>Accesar</span>
                </b-button>
              </b-form>
              <b-form @submit="submitNewPassword" v-if="requestNewPassword">
                <label
                  >Es necesario cambiar su contraseña.
                  <ul>
                    <li v-for="req in passwordRequirements" :key="req">
                      {{ req }}
                      <b-icon-question-circle
                        v-if="req.includes('símbolo')"
                        id="tooltip-password-special-chars-field"
                      />
                    </li>
                  </ul>
                  <b-tooltip
                    target="tooltip-password-special-chars-field"
                    triggers="hover"
                  >
                    {{ specialCharacters }}
                  </b-tooltip>
                </label>
                <b-form-group description="Es necesario cambiar su contraseña.">
                  <b-form-input
                    autofocus
                    v-model="newPassword"
                    :state="newPasswordState"
                    :type="passwordFieldType"
                    debounce="200"
                    required
                    placeholder="Nueva contraseña"
                  ></b-form-input>
                </b-form-group>
                <b-form-group description="Repita su nueva contraseña.">
                  <b-form-input
                    v-model="repeatedNewPassword"
                    :state="passwordsMatch"
                    :type="passwordFieldType"
                    debounce="200"
                    required
                    placeholder="Repita su nueva contraseña"
                  ></b-form-input>
                </b-form-group>
                <b-form-group
                  v-if="requestingCode"
                  description="Ingrese el código enviado por el sistema"
                >
                  <b-form-input
                    v-model="code"
                    type="number"
                    debounce="200"
                    required
                    placeholder="Ingrese el código (6 dígitos)"
                  >
                  </b-form-input>
                </b-form-group>
                <b-button
                  class="float-right"
                  variant="success"
                  type="submit"
                  :disabled="!newPasswordState || !passwordsMatch"
                  >Cambiar</b-button
                >
              </b-form>
            </b-col>
          </b-row>
        </b-container>
        <b-container class="mb-4">
          <b-row class="mt-3">
            <b-col>
              <b-button-group class="float-right">
                <b-button
                  @click="requestPasswordForgotten"
                  size="sm"
                  :disabled="!email"
                >
                  Cambiar mi contraseña
                </b-button>
              </b-button-group>
            </b-col>
            <b-col cols="12">
              <small class="float-right"
                >Ingrese su correo electronico para cambiar o recuperar su
                contraseña</small
              >
            </b-col>
          </b-row>
        </b-container>
      </main>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { pathOr } from "ramda";
import Header from "./Header";

const PASSWORD_REQUIREMENTS = [
  "Mínimo de 6 letras",
  "Mínimo de un dígito (0-9)",
  "Mínimo de una letra minúscula",
  "Mínimo de una letra mayúscula",
  "Mínimo de un símbolo especial",
];

export default {
  name: "LogIn",
  components: {
    Header,
  },
  data() {
    return {
      loading: false,
      requestNewPassword: false,
      requestingCode: false,
      code: 0,
      email: "",
      password: "",
      newPassword: "",
      repeatedNewPassword: "",
      errorMessage: "",
      passwordFieldType: "password",
      passwordRequirements: PASSWORD_REQUIREMENTS,
      specialCharacters:
        "^ $ * . [ ] { } ( ) ? \" ! @ # % & / \\ , > < ' : ; | _ ~ `",
      modalMsgBoxConfig: {
        size: "sm",
        buttonSize: "sm",
        centered: true,
        footerClass: "p-2 border-top-0",
      },
    };
  },
  async created() {
    try {
      await this.checkUserSession();
      this.navigateForward();
    } catch (err) {
      // ignore, user needs to authenticate
    }
  },
  computed: {
    newPasswordState() {
      const hasMinimumLength = this.newPassword && this.newPassword.length > 6;
      const hasNumbers = /[0-9]/.test(this.newPassword);
      const hasLowerCaseLetter = /[a-z]/.test(this.newPassword);
      const hasUpperCaseLetter = /[A-Z]/.test(this.newPassword);
      /* @see https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html */
      const hasSpecialCharacter = /(\^|\$|\*|\.|[|]|{|}|\(|\)|\?|"|!|@|#|%|&|\/|\\|,|>|<|'|:|;|\||_|~|`)/.test(
        this.newPassword
      );

      return (
        hasNumbers &&
        hasMinimumLength &&
        hasLowerCaseLetter &&
        hasUpperCaseLetter &&
        hasSpecialCharacter
      );
    },
    passwordsMatch() {
      return (
        this.repeatedNewPassword &&
        this.newPassword === this.repeatedNewPassword
      );
    },
  },
  methods: {
    ...mapGetters("auth", ["needsNewPassword"]),
    ...mapActions("auth", [
      "authenticate",
      "checkUserSession",
      "setNewPassword",
      "initForgotPassword",
      "completeForgotPassword",
    ]),
    navigateForward() {
      const route = this.$route.query.redirectTo || "/";
      this.$router.push(route);
    },
    async submitForm(evt) {
      evt.preventDefault();

      this.errorMessage = "";
      this.loading = true;
      try {
        await this.authenticate({ email: this.email, password: this.password });
        if (this.needsNewPassword()) {
          this.requestNewPassword = true;
        } else {
          this.navigateForward();
        }
      } catch (err) {
        console.error(err);
        this.errorMessage = pathOr(
          "Failed to authenticate, try again later",
          ["message"],
          err
        );
      } finally {
        this.loading = false;
      }
    },
    async submitNewPassword(evt) {
      evt.preventDefault();

      this.loading = true;
      try {
        if (!this.requestingCode) {
          await this.setNewPassword({
            password: this.newPassword,
          });
          this.navigateForward();
        } else if (this.requestingCode) {
          await this.resetPassword();
        }
      } catch (err) {
        if (err.code === "CodeMismatchException") {
          this.errorMessage =
            "El código proporsionado es inválido, intente de nuevo.";
        } else {
          this.errorMessage = err;
        }
      } finally {
        this.loading = false;
      }
    },
    async resetPassword() {
      await this.completeForgotPassword({
        email: this.email,
        password: this.newPassword,
        code: this.code,
      });
      await this.$bvModal.msgBoxOk(
        "Su contraseña ha sido cambiado exitosamente",
        this.modalMsgBoxConfig
      );
      this.requestingCode = false;
      this.requestNewPassword = false;
    },
    async requestPasswordForgotten() {
      try {
        const data = await this.initForgotPassword(this.email);
        await this.$bvModal.msgBoxOk(
          `Se ha enviado al ${data.Destination} por medio de un ${data.DeliveryMedium} un código para cambiar la contraseña`,
          this.modalMsgBoxConfig
        );
        this.requestNewPassword = true;
        this.requestingCode = true;
      } catch (err) {
        let message = "";
        if (err.code === "UserNotFoundException") {
          message = `Usuario con email ${this.email} no existe, contacte el administrador`;
        } else if (err.code === "LimitExceededException") {
          message = `Por seguridad su cuenta ha sido bloqueada temporalmente, intente más tarde o contacte al administrador del sistema`;
        } else if (err.code === "NotAuthorizedException") {
          message =
            "El usuario debe de ingresar la contraseña proporcionada por el sistema antes de poder cambiar la contraseña, contacte al administrador si no cuenta con la contraseña temporal";
        } else {
          message = `Error desconocido, por favor reporte al administrador del sistema los siguiente: ${err}`;
        }

        await this.$bvModal.msgBoxOk(message, this.modalMsgBoxConfig);
      }
    },
  },
};
</script>
